/**
 * WaitingRoom - Pre-meeting screen for guests and organizers
 *
 * Shows different states:
 * - Organizer: "Start Meeting" button
 * - Guest (scheduled): Countdown timer
 * - Guest (waiting): "Waiting for organizer" with animated dots
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Video, Clock, Play } from 'lucide-react';
import { PreJoinScreen } from './PreJoinScreen';
import { useTranslation } from '../../hooks/useTranslation';
import type { MeetingContext } from '../../stores/videoCallStore';
import { getApiEndpoints } from '@business-assistant/shared/config';
import './VideoCall.css';

// =============================================================================
// TYPES
// =============================================================================

interface WaitingRoomProps {
  meetingContext: MeetingContext;
  roomSlug: string;
  guestName: string;
  onMeetingActive: () => void;
  onStartMeeting?: () => Promise<void>;
  isOrganizer?: boolean;
}

// =============================================================================
// COUNTDOWN HELPER
// =============================================================================

function useCountdown(targetDate: string | null) {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    if (!targetDate) return;

    const update = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

// =============================================================================
// STATUS POLLING
// =============================================================================

function useStatusPolling(
  roomSlug: string,
  shouldPoll: boolean,
  onStatusChange: (status: string) => void,
) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!shouldPoll) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    const poll = async () => {
      try {
        const response = await fetch(
          `${getApiEndpoints().livekit}/rooms/${roomSlug}/status`,
          { headers: { 'Content-Type': 'application/json' } },
        );
        const data = await response.json();
        if (data.success && data.meeting) {
          onStatusChange(data.meeting.status);
        }
      } catch {
        // Ignore polling errors
      }
    };

    intervalRef.current = setInterval(poll, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [roomSlug, shouldPoll, onStatusChange]);
}

// =============================================================================
// COMPONENT
// =============================================================================

export function WaitingRoom({
  meetingContext,
  roomSlug,
  guestName,
  onMeetingActive,
  onStartMeeting,
  isOrganizer = false,
}: WaitingRoomProps) {
  const { t } = useTranslation();
  const [isStarting, setIsStarting] = useState(false);
  const [dots, setDots] = useState('');
  const countdown = useCountdown(meetingContext.scheduledAt);
  const hasCountdown = countdown !== null && meetingContext.scheduledAt;

  // Animated dots for waiting state
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Poll for status changes when guest is waiting
  const handleStatusChange = useCallback(
    (status: string) => {
      if (status === 'active') {
        onMeetingActive();
      }
    },
    [onMeetingActive],
  );

  useStatusPolling(roomSlug, !isOrganizer, handleStatusChange);

  const handleStart = async () => {
    if (!onStartMeeting) return;
    setIsStarting(true);
    try {
      await onStartMeeting();
      onMeetingActive();
    } catch (err) {
      console.error('Failed to start meeting:', err);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="videocall-waiting-room">
      {/* Meeting Title */}
      <div className="videocall-waiting-room__header">
        <div className="videocall-waiting-room__icon">
          <Video size={32} />
        </div>
        <h2 className="videocall-waiting-room__title">
          {meetingContext.title}
        </h2>
        <p className="videocall-waiting-room__name">
          {guestName}
        </p>
      </div>

      {/* Status Content */}
      <div className="videocall-waiting-room__content">
        {isOrganizer ? (
          // Organizer view: Start Meeting button
          <div className="videocall-waiting-room__organizer">
            <p className="videocall-waiting-room__message">
              {t('videocall.readyToStart')}
            </p>
            {hasCountdown && (
              <div className="videocall-waiting-room__countdown">
                <Clock size={16} />
                <span>{t('videocall.meetingStartsIn')}</span>
                <CountdownDisplay countdown={countdown} />
              </div>
            )}
            <button
              className="videocall-waiting-room__start-btn"
              onClick={handleStart}
              disabled={isStarting}
            >
              <Play size={20} />
              {isStarting ? t('common.loading') : t('videocall.startMeeting')}
            </button>
          </div>
        ) : hasCountdown ? (
          // Guest with scheduled meeting: show countdown
          <div className="videocall-waiting-room__scheduled">
            <Clock size={24} style={{ color: '#7C3AED' }} />
            <p className="videocall-waiting-room__message">
              {t('videocall.meetingStartsIn')}
            </p>
            <CountdownDisplay countdown={countdown} />
            <p className="videocall-waiting-room__sub-message">
              {t('videocall.waitingForOrganizer')}{dots}
            </p>
          </div>
        ) : (
          // Guest waiting for organizer (no countdown)
          <div className="videocall-waiting-room__waiting">
            <div className="videocall-waiting-room__dots-animation">
              <span /><span /><span />
            </div>
            <p className="videocall-waiting-room__message">
              {t('videocall.waitingForOrganizer')}{dots}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// COUNTDOWN DISPLAY
// =============================================================================

function CountdownDisplay({ countdown }: {
  countdown: { days: number; hours: number; minutes: number; seconds: number };
}) {
  const { t } = useTranslation();

  return (
    <div className="videocall-waiting-room__timer">
      {countdown.days > 0 && (
        <div className="videocall-waiting-room__timer-unit">
          <span className="videocall-waiting-room__timer-value">{countdown.days}</span>
          <span className="videocall-waiting-room__timer-label">d</span>
        </div>
      )}
      <div className="videocall-waiting-room__timer-unit">
        <span className="videocall-waiting-room__timer-value">
          {String(countdown.hours).padStart(2, '0')}
        </span>
        <span className="videocall-waiting-room__timer-label">h</span>
      </div>
      <div className="videocall-waiting-room__timer-sep">:</div>
      <div className="videocall-waiting-room__timer-unit">
        <span className="videocall-waiting-room__timer-value">
          {String(countdown.minutes).padStart(2, '0')}
        </span>
        <span className="videocall-waiting-room__timer-label">m</span>
      </div>
      <div className="videocall-waiting-room__timer-sep">:</div>
      <div className="videocall-waiting-room__timer-unit">
        <span className="videocall-waiting-room__timer-value">
          {String(countdown.seconds).padStart(2, '0')}
        </span>
        <span className="videocall-waiting-room__timer-label">s</span>
      </div>
    </div>
  );
}
